import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useNotificationStatus } from '../hooks/useNotificationStatus';
import { useSchedules } from '../query/useSchedules';
import { usePushSubscriptionsQuery } from '../query/usePushSubscriptions';
import { useUserSettingsQuery } from '../query/useUserSettings';
import { AuthContext } from '../AuthContext';

const STORAGE_KEY = 'ekkos.onboardingChecklist';

const defaultState = {
  firstTaskScheduled: false,
  notificationsPermission: 'unsupported',
  notificationsDeviceRegistered: false,
  personalizationSet: false,
  multipleDevices: false,
  themeSelected: false,
  completedAt: null,
  lastComputedAt: 0,
};

const OnboardingChecklistContext = createContext({
  state: defaultState,
  refresh: () => {},
});

function loadFromStorage() {
  if (typeof window === 'undefined') return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return { ...defaultState, ...parsed };
  } catch {
    return defaultState;
  }
}

function deriveBoolean(value, fallback) {
  if (typeof value === 'boolean') return value;
  return fallback;
}

export function OnboardingChecklistProvider({ children }) {
  const { user } = useContext(AuthContext);
  const enabled = Boolean(user);

  const notificationStatus = useNotificationStatus();
  const schedulesQuery = useSchedules({ enabled });
  const subscriptionsQuery = usePushSubscriptionsQuery({ enabled });
  const userSettingsQuery = useUserSettingsQuery({ enabled });

  const { refetch: refetchSchedules } = schedulesQuery;
  const { refetch: refetchSubscriptions } = subscriptionsQuery;
  const { refetch: refetchUserSettings } = userSettingsQuery;

  const fallbackRef = useRef(loadFromStorage());
  const [manualFlags, setManualFlags] = React.useState(() => loadFromStorage());

  const computedState = useMemo(() => {
    const fallback = fallbackRef.current;

    if (!enabled) {
      return {
        ...defaultState,
        notificationsPermission: notificationStatus,
        lastComputedAt: Date.now(),
      };
    }

    const schedules = schedulesQuery.data;
    const subscriptions = subscriptionsQuery.data;
    const settings = userSettingsQuery.data;

    const hasSchedules =
      Array.isArray(schedules) ? schedules.length > 0 : undefined;

    const enabledSubscriptions = Array.isArray(subscriptions)
      ? subscriptions.filter((sub) => !sub.disabled)
      : undefined;

    const notificationsDeviceRegistered =
      Array.isArray(subscriptions)
        ? enabledSubscriptions.length > 0
        : fallback.notificationsDeviceRegistered;

    const multipleDevices =
      Array.isArray(subscriptions)
        ? enabledSubscriptions.length > 1
        : fallback.multipleDevices;

    const personalizationSet = settings
      ? Boolean(
          settings.retryPolicy &&
            settings.personality &&
            settings.daytimePreference
        )
      : fallback.personalizationSet;

    return {
      firstTaskScheduled: deriveBoolean(
        typeof hasSchedules === 'boolean' ? hasSchedules : undefined,
        fallback.firstTaskScheduled
      ),
      notificationsPermission: notificationStatus,
      notificationsDeviceRegistered,
      personalizationSet,
      multipleDevices,
      themeSelected: deriveBoolean(manualFlags.themeSelected, fallback.themeSelected),
      completedAt: fallback.completedAt ?? null,
      lastComputedAt: Date.now(),
    };
  }, [
    enabled,
    notificationStatus,
    schedulesQuery.data,
    subscriptionsQuery.data,
    userSettingsQuery.data,
    manualFlags.themeSelected,
  ]);

  useEffect(() => {
    fallbackRef.current = computedState;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(computedState));
    } catch {
      // Ignore write failures (e.g., private mode)
    }
  }, [computedState]);

  const markThemeSelected = useCallback(() => {
    setManualFlags((prev) => {
      if (prev.themeSelected) return prev;
      const next = { ...prev, themeSelected: true };
      fallbackRef.current = { ...fallbackRef.current, themeSelected: true };
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...computedState, themeSelected: true }));
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, [computedState]);

  const refresh = useCallback(() => {
    if (!enabled) return;
    refetchSchedules?.();
    refetchSubscriptions?.();
    refetchUserSettings?.();
  }, [
    enabled,
    refetchSchedules,
    refetchSubscriptions,
    refetchUserSettings,
  ]);

  const value = useMemo(
    () => ({
      state: computedState,
      refresh,
      markThemeSelected,
    }),
    [computedState, refresh, markThemeSelected]
  );

  return (
    <OnboardingChecklistContext.Provider value={value}>
      {children}
    </OnboardingChecklistContext.Provider>
  );
}

export function useOnboardingChecklist() {
  const ctx = useContext(OnboardingChecklistContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingChecklist must be used within OnboardingChecklistProvider'
    );
  }
  return ctx;
}
